// src/pages/Payment.tsx
// Final step - Stripe checkout
// Navigates to /success after successful payment
// Styled to match app dashboard

import { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { useNavigate } from "react-router-dom";
import { currentUser, getSupabase } from "@/lib/supabase";
import { ENV } from "@/lib/env";

const stripePromise = loadStripe(ENV.STRIPE_PUBLISHABLE!);

export default function Payment() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState("");

  useEffect(() => {
    (async () => {
      const u = await currentUser();
      if (!u) {
        navigate("/signup");
        return;
      }
      setUser(u);
      
      // Get phone for welcome message
      const { data } = await getSupabase()
        .from("clients")
        .select("phone")
        .eq("user_id", u.id)
        .single();
      if (data?.phone) setPhone(data.phone);
    })();
  }, [navigate]);

  const handleCheckout = async () => {
    setLoading(true);

    try {
      const res = await fetch(`${ENV.EDGE_BASE}/create-checkout-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          price_id: "price_1SuwjI7Fd3suzixkaeYhviPd",
          user_id: user.id,
          email: user.email,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.sessionId) {
        throw new Error(data.error || "Failed to create checkout session");
      }

      const stripe = await stripePromise;
      await stripe?.redirectToCheckout({ sessionId: data.sessionId });
    } catch (err: any) {
      console.error("Checkout error:", err);
      alert(err.message || "Failed to start checkout");
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#ffffff] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#a78bfa] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#ffffff] text-[#0f172a]">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0]">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-gray-500 hover:text-[#0f172a] transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-sm">Back</span>
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#a78bfa] flex items-center justify-center">
            <svg className="w-5 h-5 text-[#0f172a]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M13 10V3L4 14h7v7l9-11h-7z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <span className="font-semibold text-[#0f172a]">Input</span>
        </div>
        <div className="w-16"></div>
      </header>

      {/* Main Content */}
      <main className="flex flex-col items-center px-4 py-8">
        <div className="w-full max-w-md">
          
          {/* Title */}
          <div className="text-center mb-6">
            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-[#a78bfa]/10 border border-[#a78bfa]/30 flex items-center justify-center">
              <svg className="w-7 h-7 text-[#a78bfa]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-[#0f172a] mb-2">
              You're almost there
            </h1>
            <p className="text-gray-500 text-sm">
              Start your free trial today
            </p>
          </div>

          {/* Pricing Card */}
          <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-6">
            
            {/* Trial Badge */}
            <div className="flex justify-center mb-5">
              <span className="px-4 py-1.5 bg-[#a78bfa]/10 border border-[#a78bfa]/30 rounded-full text-[#a78bfa] text-sm font-medium">
                7-day free trial
              </span>
            </div>
            
            {/* Price */}
            <div className="text-center mb-5">
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-4xl font-bold text-[#0f172a]">£2.99</span>
                <span className="text-gray-500 text-base">/week</span>
              </div>
              <p className="text-gray-600 text-xs mt-2">
                Billed weekly after trial • Cancel anytime
              </p>
            </div>

            {/* Features */}
            <div className="space-y-3 mb-6">
              {[
                "Personalised Nutrition Plans",
                "Custom Calorie/Macro Targets",
                "Personalised Training Plans",
                "Endurance Plans Auto Sync To Garmin",
                "WhatsApp Coach For Daily Support",
                "Weekly Progress Reviews",
                "Wearable Sync (Garmin, WHOOP)",
              ].map((feature, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#a78bfa]/10 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-[#a78bfa]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-gray-600 text-sm">{feature}</span>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <button
              onClick={handleCheckout}
              disabled={loading}
              className="w-full h-12 bg-[#a78bfa] hover:bg-[#a78bfa]/90 disabled:bg-[#a78bfa]/50 text-[#0f172a] font-semibold text-base rounded-xl transition-all duration-200 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Loading...
                </>
              ) : "Start Free Trial"}
            </button>
          </div>

          {/* Trust Badges */}
          <div className="flex items-center justify-center gap-4 text-gray-600 text-xs mt-6">
            <div className="flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Secure checkout
            </div>
            <span className="text-[#475569]">•</span>
            <div className="flex items-center gap-1.5">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
              </svg>
              Powered by Stripe
            </div>
          </div>

          {/* Final step indicator */}
          <div className="mt-8 text-center">
            <p className="text-sm text-[#a78bfa] font-medium flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Final step — you're all set up!
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-4 text-center border-t border-[#e2e8f0]">
        <p className="text-gray-600 text-xs">
          © {new Date().getFullYear()} Input
        </p>
      </footer>
    </div>
  );
}
